// Readers for one cell of an imported row. Each takes the text exactly as the parser
// returned it and gives a typed value or a stable code, never a sentence (result.ts).
//
// Shared rules, so every importer treats the same text the same way:
//   - Whitespace around a value is removed; whitespace inside it is kept.
//   - An empty cell (after trimming) is missing. Base readers refuse it with
//     `cell.required`; wrap them in `optional()` or `withDefault()` to allow it.
//   - Control characters (tabs, line breaks and the rest of C0, plus DEL) are refused in
//     single-line values, so a stray line break inside quotes can't end up in a title.
//   - Dates are ISO only (YYYY-MM-DD). "03/04/2026" is 3 April in Kenya and 4 March in a
//     spreadsheet set to US format, and the importer has no way to know which was meant.

import { parseIsoDay } from '../time.ts';
import type { CalendarDay } from '../time.ts';

export type CellErrorCode =
  | 'cell.required'
  | 'cell.too_long'
  | 'cell.control_character'
  | 'cell.not_allowed'
  | 'cell.not_yes_no'
  | 'cell.not_integer'
  | 'cell.out_of_range'
  | 'cell.invalid_date'
  | 'cell.too_many_items';

export type CellWarningCode = 'cell.duplicate_item_dropped';

export const CELL_MESSAGES: Record<CellErrorCode | CellWarningCode, string> = {
  'cell.required': 'This value is required',
  'cell.too_long': 'This value is too long',
  'cell.control_character': 'This value contains a line break or tab. Keep it on one line.',
  'cell.not_allowed': 'This value is not one of the allowed values',
  'cell.not_yes_no': 'Write yes or no',
  'cell.not_integer': 'Write a whole number, without commas or spaces',
  'cell.out_of_range': 'This number is outside the allowed range',
  'cell.invalid_date': 'Write the date as YYYY-MM-DD, for example 2026-09-22',
  'cell.too_many_items': 'This list has too many items',
  'cell.duplicate_item_dropped': 'A repeated item in this list was left out',
};

export type CellResult<T> =
  | { readonly ok: true; readonly value: T; readonly warning?: CellWarningCode }
  | { readonly ok: false; readonly code: CellErrorCode };

export type CellReader<T> = (raw: string) => CellResult<T>;

function value<T>(v: T, warning?: CellWarningCode): CellResult<T> {
  return warning === undefined ? { ok: true, value: v } : { ok: true, value: v, warning };
}

function problem<T>(code: CellErrorCode): CellResult<T> {
  return { ok: false, code };
}

// C0 controls and DEL. Deliberately not the wider Unicode "Cc" class; those are rare in
// practice and the parser already refuses NUL.
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u007f]/;

/** Whether a cell counts as missing: empty or only whitespace. */
export function isBlank(raw: string): boolean {
  return raw.trim() === '';
}

export interface TextOptions {
  /** Maximum length in characters (code points), after trimming. */
  readonly maxLength: number;
  /** Allow line breaks and tabs, for multi-line free text. Default false. */
  readonly multiline?: boolean;
}

/** A required piece of text, trimmed. */
export function text(options: TextOptions): CellReader<string> {
  return (raw) => {
    const trimmed = raw.trim();
    if (trimmed === '') return problem('cell.required');
    if (!options.multiline && CONTROL.test(trimmed)) return problem('cell.control_character');
    if (Array.from(trimmed).length > options.maxLength) return problem('cell.too_long');
    return value(trimmed);
  };
}

/**
 * One of a fixed set of values, compared without regard to case or surrounding spaces.
 * Returns the value as written in `allowed`, so "Active" and "ACTIVE" both give "active".
 */
export function oneOf<const V extends string>(allowed: readonly V[]): CellReader<V> {
  const byKey = new Map(allowed.map((v) => [v.toLowerCase(), v] as const));
  return (raw) => {
    const trimmed = raw.trim();
    if (trimmed === '') return problem('cell.required');
    const found = byKey.get(trimmed.toLowerCase());
    return found === undefined ? problem('cell.not_allowed') : value(found);
  };
}

const YES = new Set(['yes', 'true']);
const NO = new Set(['no', 'false']);

/** Yes or no. "true" and "false" are accepted too, because spreadsheets write TRUE and FALSE. */
export function yesNo(): CellReader<boolean> {
  return (raw) => {
    const key = raw.trim().toLowerCase();
    if (key === '') return problem('cell.required');
    if (YES.has(key)) return value(true);
    if (NO.has(key)) return value(false);
    return problem('cell.not_yes_no');
  };
}

export interface IntegerOptions {
  readonly min: number;
  readonly max: number;
}

/** A whole number written with digits and an optional leading minus sign. */
export function integer(options: IntegerOptions): CellReader<number> {
  return (raw) => {
    const trimmed = raw.trim();
    if (trimmed === '') return problem('cell.required');
    if (!/^-?\d+$/.test(trimmed)) return problem('cell.not_integer');
    const n = Number(trimmed);
    if (!Number.isSafeInteger(n) || n < options.min || n > options.max)
      return problem('cell.out_of_range');
    return value(n);
  };
}

/** A calendar date written as YYYY-MM-DD. */
export function isoDay(): CellReader<CalendarDay> {
  return (raw) => {
    if (raw.trim() === '') return problem('cell.required');
    const parsed = parseIsoDay(raw);
    return parsed.ok ? value(parsed.value) : problem('cell.invalid_date');
  };
}

export interface ListOptions {
  /** The character between items, such as "|". */
  readonly separator: string;
  readonly maxItems: number;
  readonly maxItemLength: number;
}

/**
 * A list of text items in one cell. Items are trimmed; empty items are ignored; an item
 * that repeats an earlier one (ignoring case) is left out with a warning. An empty cell
 * is an empty list, not missing, because a list can legitimately have no items.
 */
export function list(options: ListOptions): CellReader<string[]> {
  if (options.separator === '') throw new Error('list(): separator must not be empty');
  const item = text({ maxLength: options.maxItemLength });
  return (raw) => {
    const items: string[] = [];
    const seen = new Set<string>();
    let warning: CellWarningCode | undefined;
    for (const part of raw.split(options.separator)) {
      if (isBlank(part)) continue;
      const read = item(part);
      if (!read.ok) return read;
      const key = read.value.toLowerCase();
      if (seen.has(key)) {
        warning = 'cell.duplicate_item_dropped';
        continue;
      }
      seen.add(key);
      items.push(read.value);
    }
    if (items.length > options.maxItems) return problem('cell.too_many_items');
    return value(items, warning);
  };
}

/** Allows the cell to be empty, giving null. */
export function optional<T>(reader: CellReader<T>): CellReader<T | null> {
  return (raw) => (isBlank(raw) ? value(null) : reader(raw));
}

/** Allows the cell to be empty, giving `fallback`. */
export function withDefault<T>(reader: CellReader<T>, fallback: T): CellReader<T> {
  return (raw) => (isBlank(raw) ? value(fallback) : reader(raw));
}
